import config from 'config';

export type ResultStatus = 'not_started' | 'processing' | 'ready' | 'failed';
export type ReviewStatus = 'pending' | 'reviewed';
export type GenerateMode = 'refine' | 'regenerate';
export type PortalDocumentWorkflowStatus = 'uploaded' | 'processing' | 'analysed' | 'completed';

export interface PortalDocumentResultResponse {
    documentId: string;
    resultStatus: ResultStatus;
    html: string | null;
    version: number | null;
    updatedAt: string | null;
    reviewStatus: ReviewStatus;
}

export interface PortalDocumentGenerateRequest {
    prompt: string;
    mode: GenerateMode;
    includeAnalysisText: boolean;
    baseVersion: number | null;
    currentHtml?: string;
}

export interface PortalDocumentGenerateResponse {
    jobId: string;
    documentId: string;
    resultStatus: 'processing';
}

export interface PortalDocumentGenerateJobResponse {
    jobId: string;
    status: 'processing' | 'completed' | 'failed';
    html: string | null;
    version: number | null;
    updatedAt: string | null;
    errorMessage: string | null;
}

export interface PortalDocumentReviewResponse {
    documentId: string;
    status: PortalDocumentWorkflowStatus;
    reviewStatus: ReviewStatus;
    updatedAt: string;
    emailNotification?: {
        sent: boolean;
        recipient: string;
        sentAt?: string | null;
    };
}

type PortalDocumentsResultHeaders = {
    Authorization: string;
    'Accept-Language': string;
    'Content-Type': string;
};

function buildResultUrl(documentId: string): string {
    return `${config.api.base}${config.api.portalDocuments}/${documentId}/result`;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }

    return response.json() as Promise<T>;
}

export async function fetchPortalDocumentResult(
    documentId: string,
    headers: PortalDocumentsResultHeaders,
): Promise<PortalDocumentResultResponse> {
    const response = await fetch(buildResultUrl(documentId), { headers });
    return parseJsonResponse<PortalDocumentResultResponse>(response);
}

export async function generatePortalDocumentResult(
    documentId: string,
    headers: PortalDocumentsResultHeaders,
    payload: PortalDocumentGenerateRequest,
): Promise<PortalDocumentGenerateResponse> {
    const response = await fetch(`${buildResultUrl(documentId)}/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });

    return parseJsonResponse<PortalDocumentGenerateResponse>(response);
}

export async function fetchPortalDocumentResultJob(
    documentId: string,
    jobId: string,
    headers: PortalDocumentsResultHeaders,
): Promise<PortalDocumentGenerateJobResponse> {
    const response = await fetch(`${buildResultUrl(documentId)}/jobs/${jobId}`, {
        headers,
    });

    return parseJsonResponse<PortalDocumentGenerateJobResponse>(response);
}

export async function reviewPortalDocumentResult(
    documentId: string,
    headers: PortalDocumentsResultHeaders,
    reviewStatus: ReviewStatus,
    notifyCustomer = false,
): Promise<PortalDocumentReviewResponse> {
    const response = await fetch(`${buildResultUrl(documentId)}/review`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reviewStatus, notifyCustomer }),
    });

    return parseJsonResponse<PortalDocumentReviewResponse>(response);
}
